export type PraxisChecklistStatus = 'OK' | 'NOK' | 'N/A';

export interface PraxisContato {
  nome: string;
  funcao: string;
  contato: string;
}

export interface PraxisLimiteMaquinaItem {
  codigo: string;
  titulo: string;
  texto: string;
}

export interface PraxisChecklistItem {
  codigo: string;
  descricao: string;
  status: PraxisChecklistStatus;
  observacao?: string;
}

export interface PraxisChecklistGrupo {
  titulo: string;
  itens: PraxisChecklistItem[];
}

export interface PraxisChecklistTecnicoAdmin {
  versao: string;
  grupos: PraxisChecklistGrupo[];
}

export interface PraxisCamposDocumento {
  versaoTemplate: string;
  numeroDocumentoPraxis: string;
  anoCopyright: string;
  clienteFinal: string;
  descricaoServico: string;
  contatosCliente: PraxisContato[];
  contatosSchmersal: PraxisContato[];
  normasNacionais: string[];
  normasInternacionais: string[];
  limitesMaquina: PraxisLimiteMaquinaItem[];
}

export interface PraxisCamposClienteWrapper {
  praxis: PraxisCamposDocumento;
}

export interface PraxisInputFieldDefinition {
  chave: string;
  label: string;
  obrigatorio: boolean;
  origem: 'topo' | 'apreciacao' | 'risco';
}

export interface PraxisInputSectionDefinition {
  secao: string;
  descricao: string;
  campos: PraxisInputFieldDefinition[];
}

export interface PraxisCampoPendente {
  secao: string;
  chave: string;
  label: string;
}

export const PRAXIS_TEMPLATE_VERSION = 'praxis-2026-01';

export const PRAXIS_LIMITES_PADRAO: PraxisLimiteMaquinaItem[] = [
  'SITE',
  'LOCAL DE INSTALAÇÃO',
  'NOME DA GERÊNCIA',
  'CÓD. EQUIPAMENTO Nº SAP',
  'CAPACIDADE MÁXIMA',
  'MODELO',
  'FABRICANTE',
  'ANO DE FABRICAÇÃO',
  'NÚMERO DE PATRIMÔNIO',
  'CONTRATO/ Nº. OS',
  'DIMENSÕES DO EQUIPAMENTO',
  'AMBIENTE',
  'PISO OPERACIONAL',
  'PESO BRUTO DA MÁQUINA (KG)',
  'ACIONAMENTOS HIDRÁULICOS',
  'ACIONAMENTOS PNEUMÁTICOS',
  'ACIONAMENTOS ELÉTRICOS',
  'POTÊNCIA',
  'Nº. DE MOTORES/ REDUTORES',
  'Nº. DE MOTOREDUTORES',
  'TENSÃO DE ALIMENTAÇÃO',
  'TENSÃO DE COMANDO',
  'CORRENTE',
  'VELOCIDADE MÁXIMA',
  'SISTEMA DE MANUSEIO/ TRANSPORTE/ MOVIMENTAÇÃO DE CARGA/ PEÇA/ PRODUTO',
  'FIXAÇÃO DA MÁQUINA, CRITÉRIO DE AVALIAÇÃO',
].map((titulo, index) => ({
  codigo: String(index + 1).padStart(2, '0'),
  titulo,
  texto: '',
}));

export const PRAXIS_LIMITES_ESPACO_PADRAO = `A planta deve permitir o livre acesso a todos os pontos de operação e manutenção da máquina, conforme atividades a serem realizadas, mesmo com as proteções abertas. Deve-se respeitar um espaçamento ao longo do perímetro da máquina com 1200mm para circulação, principalmente se forem acessos principais e/ou rotas de fuga; para outros acessos, o valor mínimo aceitável é de 600mm.`;

export const PRAXIS_LIMITES_ADICIONAIS_PADRAO = `O equipamento requer operadores treinados e qualificados, com conhecimento das funções de operação, segurança e manutenção do equipamento. A operação do equipamento deve ser realizada somente em ambiente seco, protegido de influências externas e intempéries. Intervenções de limpeza e/ou manutenção devem ser informadas previamente, planejadas e cumpridas conforme manual e/ou orientação técnica de um responsável técnico.`;

export const PRAXIS_CICLOS_VIDA_PADRAO = [
  'Ajuste',
  'Limpeza',
  'Manutenção',
  'Operação Normal',
] as const;

export const PRAXIS_TIPOS_PERIGO_PADRAO = [
  'Perigos Mecânicos',
  'Perigos Elétricos',
  'Perigos Ergonômicos',
  'Perigos Térmicos',
  'Perigos Químicos',
  'Ruído e Vibração',
] as const;

const CHECKLIST_OFICIAL: Array<[string, string, PraxisChecklistStatus, string]> = [
  ['10', 'Legal: Os requisitos de segurança e saúde locais (do país onde se encontra a máquina) são mais rígidos que os requisitos internos da empresa?', 'N/A', ''],
  ['11', 'Legal: Caso as normas do país sejam mais rigorosas do que as normas da empresa, estas normas são cumpridas?', 'N/A', ''],
  ['12', 'Legal: No caso da origem ou destino da máquina ser a Europa, possui declaração de conformidade CE?', 'N/A', ''],
  ['13', 'Legal: A empresa possui relação atualizada das máquinas e equipamentos à disposição da Auditoria Fiscal do Trabalho?', 'N/A', ''],
  ['20', 'Área classificada: A máquina se encontra em área classificada, no caso de gases inflamáveis, de acordo com a norma ABNT NBR IEC 60079-10-1?', 'N/A', ''],
  ['21', 'Área classificada: Se sim para a pergunta 20, a máquina possui o estudo (ou planta) de área classificada com a respectiva ART realizada por profissional habilitado?', 'N/A', ''],
  ['22', 'Área classificada: Se sim para a pergunta 20, a máquina possui registro de inspeção das instalações elétricas EX?', 'N/A', ''],
  ['23', 'Área classificada: A máquina se encontra em área classificada, no caso de poeiras combustíveis, de acordo com a norma ABNT NBR IEC 60079-10-2?', 'N/A', ''],
  ['24', 'Área classificada: Se sim para a pergunta 23, a máquina possui estudo (ou planta) de área classificada com ART de profissional habilitado?', 'N/A', ''],
  ['25', 'Área classificada: Se sim para a pergunta 23, a máquina possui registro de inspeção das instalações elétricas EX?', 'N/A', ''],
  ['30', 'Administrativa: A máquina possui uma Apreciação de Riscos prévia realizada de acordo com a ABNT NBR ISO 12100?', 'NOK', 'Não evidenciado'],
  ['31', 'Administrativa: A máquina possui manual de instruções de operação / de segurança / de manutenção em português?', 'NOK', 'Não evidenciado'],
  ['32', 'Administrativa: A máquina possui esquema elétrico atualizado em português?', 'NOK', 'Não evidenciado'],
  ['33', 'Administrativa: A máquina possui esquema mecânico atualizado em português?', 'NOK', 'Não evidenciado'],
  ['34', 'Administrativa: A máquina possui esquema pneumático em português?', 'NOK', 'Não evidenciado'],
  ['35', 'Administrativa: A máquina possui esquema hidráulico atualizado em português?', 'N/A', ''],
  ['36', 'Administrativa: A máquina possui plano de manutenção preventiva / preditiva?', 'NOK', 'Não evidenciado'],
  ['37', 'Administrativa: A máquina possui registro de manutenção corretiva?', 'NOK', 'Não evidenciado'],
  ['38', 'Administrativa: A máquina possui plano de bloqueio / etiquetamento (LockOut / TagOut)?', 'NOK', 'Não evidenciado'],
  ['39', 'Administrativa: A máquina possui registro de treinamento de operação e segurança emitido pelo RH ou setor equivalente?', 'NOK', 'Não evidenciado'],
  ['40', 'Administrativa: A máquina possui registro de fornecimento de EPI adequado contendo Certificado de Aprovação - CA e assinatura de recebimento?', 'NOK', 'Não evidenciado'],
  ['41', 'Administrativa: A máquina possui laudo de medição de níveis de ruído?', 'NOK', 'Não evidenciado'],
  ['42', 'Administrativa: A máquina possui laudo de medição de iluminância?', 'NOK', 'Não evidenciado'],
  ['43', 'Administrativa: A máquina possui laudo de medição de radiação ionizante e / ou radiação não ionizante?', 'N/A', ''],
  ['44', 'Administrativa: A máquina possui laudo de substâncias perigosas / FISPQ?', 'NOK', 'Não evidenciado'],
  ['45', 'Administrativa: A máquina possui laudo de medição do tempo de parada?', 'N/A', ''],
  ['46', 'Administrativa: A máquina possui laudo de medição de aterramento elétrico?', 'NOK', 'Não evidenciado'],
  ['47', 'Administrativa: A máquina possui documento de autorização para entrada em espaço confinado quando aplicável?', 'OK', ''],
  ['48', 'Administrativa: A máquina possui laudo de compatibilidade eletromagnética (EMC)?', 'NOK', 'Não evidenciado'],
  ['49', 'Administrativa: A máquina possui laudo de análise de risco ergonômico?', 'NOK', 'Não evidenciado'],
  ['50', 'Administrativa: A máquina possui histórico de acidentes de trabalho?', 'NOK', 'Não evidenciado'],
  ['51', 'Administrativa: A máquina necessita de operador de empilhadeira para operação normal ou ajuste?', 'N/A', ''],
  ['52', 'Administrativa: O operador de empilhadeira possui cartão de autorização para sua condução?', 'N/A', ''],
  ['53', 'Administrativa: A máquina possui análise de risco para trabalho em altura com procedimento e controle administrativo?', 'N/A', ''],
];

function criarGrupoChecklistOficial(titulo: string, codigos: string[]): PraxisChecklistGrupo {
  return {
    titulo,
    itens: CHECKLIST_OFICIAL
      .filter(([codigo]) => codigos.includes(codigo))
      .map(([codigo, descricao, status, observacao]) => ({
        codigo,
        descricao,
        status,
        observacao,
      })),
  };
}

export const PRAXIS_CHECKLIST_PADRAO: PraxisChecklistGrupo[] = [
  criarGrupoChecklistOficial('Legal', ['10', '11', '12', '13']),
  criarGrupoChecklistOficial('Área classificada', ['20', '21', '22', '23', '24', '25']),
  criarGrupoChecklistOficial('Administrativa', [
    '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41',
    '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53',
  ]),
];

export const PRAXIS_CAMPOS_LIMITE_ANTIGOS: PraxisLimiteMaquinaItem[] = [
  'DESCRICAO DA MAQUINA',
  'TIPO DE OPERACAO',
  'PRODUTO / MATERIAL PROCESSADO',
  'CAPACIDADE NOMINAL',
  'VELOCIDADE / CICLO',
  'ENERGIAS UTILIZADAS',
  'MODOS DE OPERACAO',
  'INTERFACE HOMEM-MAQUINA',
].map((titulo, index) => ({
  codigo: `A${String(index + 1).padStart(2, '0')}`,
  titulo,
  texto: '',
}));

export const PRAXIS_DOCUMENT_INPUT_SECTIONS: PraxisInputSectionDefinition[] = [
  {
    secao: 'Capa',
    descricao: 'Campos que mudam na abertura do documento.',
    campos: [
      { chave: 'numeroDocumento', label: 'N do documento Praxis', obrigatorio: true, origem: 'topo' },
      { chave: 'clienteFinal', label: 'Cliente final', obrigatorio: true, origem: 'topo' },
      { chave: 'descricaoServico', label: 'Descricao do servico', obrigatorio: true, origem: 'topo' },
      { chave: 'dataEmissao', label: 'Data de emissao', obrigatorio: true, origem: 'topo' },
      { chave: 'consultorTecnico', label: 'Consultor tecnico / criado por', obrigatorio: true, origem: 'topo' },
    ],
  },
  {
    secao: 'Informacoes do Documento',
    descricao: 'Pessoas de contato e ajustes de referencia do cliente.',
    campos: [
      { chave: 'contatosCliente', label: 'Contatos do cliente', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'contatosSchmersal', label: 'Contatos Schmersal/tec.nicum', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'normasNacionais', label: 'Normas nacionais complementares', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'normasInternacionais', label: 'Normas internacionais complementares', obrigatorio: false, origem: 'apreciacao' },
    ],
  },
  {
    secao: 'Cadastro da Maquina',
    descricao: 'Identificacao da maquina e limites que variam por ativo.',
    campos: [
      { chave: 'nomeMaquina', label: 'Maquina', obrigatorio: true, origem: 'topo' },
      { chave: 'tipoMaquina', label: 'Tipo', obrigatorio: true, origem: 'topo' },
      { chave: 'identificacaoMaquina', label: 'Identificacao', obrigatorio: true, origem: 'topo' },
      { chave: 'setorMaquina', label: 'Setor', obrigatorio: true, origem: 'topo' },
      { chave: 'revisao', label: 'Revisao', obrigatorio: true, origem: 'topo' },
      { chave: 'funcaoMaquina', label: 'Funcao da maquina', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'fotoPanoramicaUrl', label: 'Foto panoramica', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'fotoIdentificacaoUrl', label: 'Foto de identificacao', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'limitesMaquina', label: 'Lista numerada de limites da maquina', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'limitesEspaco', label: 'Limites de espaco', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'limitesAdicionais', label: 'Limites adicionais', obrigatorio: true, origem: 'apreciacao' },
    ],
  },
  {
    secao: 'Checklist e Fechamento',
    descricao: 'Campos editoriais finais do documento.',
    campos: [
      { chave: 'checklistTecnicoAdmin', label: 'Checklist tecnico/administrativo', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'conclusoesFinais', label: 'Conclusoes finais', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'exclusaoResponsabilidade', label: 'Exclusao de responsabilidade', obrigatorio: false, origem: 'apreciacao' },
      { chave: 'cidadeAssinatura', label: 'Cidade da assinatura', obrigatorio: true, origem: 'apreciacao' },
      { chave: 'dataAprovacao', label: 'Data do fechamento', obrigatorio: true, origem: 'apreciacao' },
    ],
  },
  {
    secao: 'Fichas de Risco',
    descricao: 'Entradas que se repetem para cada parte/perigo avaliado.',
    campos: [
      { chave: 'parte', label: 'Parte / area avaliada', obrigatorio: true, origem: 'risco' },
      { chave: 'tipoPerigo', label: 'Tipo de perigo', obrigatorio: true, origem: 'risco' },
      { chave: 'eventoPerigoso', label: 'Evento perigoso', obrigatorio: true, origem: 'risco' },
      { chave: 'perigosEncontrados', label: 'Perigos encontrados', obrigatorio: true, origem: 'risco' },
      { chave: 'consequencias', label: 'Potenciais consequencias', obrigatorio: true, origem: 'risco' },
      { chave: 'cicloVidaArray', label: 'Ciclo de vida', obrigatorio: true, origem: 'risco' },
      { chave: 'fotoAtualUrl1..4', label: 'Fotos da parte', obrigatorio: false, origem: 'risco' },
      { chave: 'PO/FE/GS/NP atual e futuro', label: 'Parametros HRN atual e futuro', obrigatorio: true, origem: 'risco' },
      { chave: 'adequacoesTecnicas', label: 'Adequacoes tecnicas', obrigatorio: false, origem: 'risco' },
      { chave: 'adequacoesNormativas', label: 'Adequacoes administrativas/normativas', obrigatorio: false, origem: 'risco' },
    ],
  },
];

export function criarPraxisCamposDocumentoDefault(clienteFinal = 'SENAI'): PraxisCamposDocumento {
  return {
    versaoTemplate: PRAXIS_TEMPLATE_VERSION,
    numeroDocumentoPraxis: '',
    anoCopyright: '2025',
    clienteFinal,
    descricaoServico: '',
    contatosCliente: [],
    contatosSchmersal: [],
    normasNacionais: [],
    normasInternacionais: [],
    limitesMaquina: PRAXIS_LIMITES_PADRAO.map((item) => ({ ...item })),
  };
}

function temValor(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null && value !== '';
}

function obterValorPraxis(dados: any, chave: string): unknown {
  const praxis = dados?.camposCliente?.praxis || {};
  switch (chave) {
    case 'numeroDocumento':
      return dados?.numeroDocumento || praxis.numeroDocumentoPraxis;
    case 'clienteFinal':
    case 'anoCopyright':
    case 'descricaoServico':
    case 'contatosCliente':
    case 'contatosSchmersal':
    case 'normasNacionais':
    case 'normasInternacionais':
    case 'limitesMaquina':
      return praxis[chave];
    case 'consultorTecnico':
      return dados?.executanteUserId || dados?.executanteExterno || dados?.responsavelTecnicoUserId || dados?.responsavelTecnicoExterno;
    case 'checklistTecnicoAdmin':
      return dados?.checklistTecnicoAdmin?.grupos;
    default:
      return dados?.[chave];
  }
}

export function coletarPendenciasPraxis(dados: any): PraxisCampoPendente[] {
  const pendencias: PraxisCampoPendente[] = [];

  for (const secao of PRAXIS_DOCUMENT_INPUT_SECTIONS) {
    for (const campo of secao.campos) {
      if (!campo.obrigatorio || campo.origem === 'risco') continue;
      if (!temValor(obterValorPraxis(dados, campo.chave))) {
        pendencias.push({
          secao: secao.secao,
          chave: campo.chave,
          label: campo.label,
        });
      }
    }
  }

  const limites = dados?.camposCliente?.praxis?.limitesMaquina;
  if (Array.isArray(limites)) {
    const limitesVazios = limites.filter((item) => !temValor(item?.texto)).length;
    if (limitesVazios > 0) {
      pendencias.push({
        secao: 'Cadastro da Maquina',
        chave: 'limitesMaquina.textos',
        label: `${limitesVazios} limite(s) da máquina sem texto`,
      });
    }
  }

  return pendencias;
}

export function normalizarPraxisCamposDocumento(
  valor: unknown,
  clienteFinal = 'SENAI',
): PraxisCamposDocumento {
  const base = criarPraxisCamposDocumentoDefault(clienteFinal);
  const origem = (valor && typeof valor === 'object' ? valor : {}) as Partial<PraxisCamposDocumento>;

  return {
    versaoTemplate: origem.versaoTemplate || base.versaoTemplate,
    numeroDocumentoPraxis: origem.numeroDocumentoPraxis || '',
    anoCopyright: origem.anoCopyright || base.anoCopyright,
    clienteFinal: origem.clienteFinal || clienteFinal,
    descricaoServico: origem.descricaoServico || '',
    contatosCliente: Array.isArray(origem.contatosCliente) ? origem.contatosCliente : [],
    contatosSchmersal: Array.isArray(origem.contatosSchmersal) ? origem.contatosSchmersal : [],
    normasNacionais: Array.isArray(origem.normasNacionais) ? origem.normasNacionais : [],
    normasInternacionais: Array.isArray(origem.normasInternacionais) ? origem.normasInternacionais : [],
    limitesMaquina: Array.isArray(origem.limitesMaquina) && origem.limitesMaquina.length > 0
      ? origem.limitesMaquina.map((item, index) => ({
          codigo: item?.codigo || base.limitesMaquina[index]?.codigo || String(index + 1).padStart(2, '0'),
          titulo: item?.titulo || base.limitesMaquina[index]?.titulo || 'ITEM',
          texto: item?.texto || '',
        }))
      : base.limitesMaquina,
  };
}

export function criarChecklistPraxisPadrao(): PraxisChecklistTecnicoAdmin {
  return {
    versao: PRAXIS_TEMPLATE_VERSION,
    grupos: PRAXIS_CHECKLIST_PADRAO.map((grupo) => ({
      ...grupo,
      itens: grupo.itens.map((item) => ({ ...item })),
    })),
  };
}

export function normalizarChecklistPraxis(valor: unknown): PraxisChecklistTecnicoAdmin {
  const checklist = valor && typeof valor === 'object' ? valor as Partial<PraxisChecklistTecnicoAdmin> : {};
  if (Array.isArray(checklist.grupos) && checklist.grupos.length > 0) {
    const itens = checklist.grupos.flatMap((grupo) => grupo.itens || []);
    const usaCodigosOficiais = itens.length > 0 && itens.every((item) => /^\d+$/.test(String(item.codigo || '').trim()));
    if (!usaCodigosOficiais) return criarChecklistPraxisPadrao();

    return {
      versao: checklist.versao || PRAXIS_TEMPLATE_VERSION,
      grupos: checklist.grupos.map((grupo) => ({
        titulo: grupo.titulo || 'Grupo',
        itens: Array.isArray(grupo.itens)
          ? grupo.itens.map((item, index) => ({
              codigo: item.codigo || `I.${index + 1}`,
              descricao: item.descricao || '',
              status: item.status || 'N/A',
              observacao: item.observacao || '',
            }))
          : [],
      })),
    };
  }

  return criarChecklistPraxisPadrao();
}
